/**
 * rivgent-search-dedup.js
 *
 * Fix for Shopify search pagination non-determinism.
 *
 * Background:
 *   Shopify's `/search?q=...&page=2` ranking is unstable — the same URL can
 *   return different result sets across requests, causing products from page 1
 *   to reappear on page 2 (intersection of 0..6 items in our case).
 *
 * Fix:
 *   Listen for the `products:update` event that facets.min.js dispatches after
 *   an AJAX pagination swap, then dedupe product cards within `[main-content]`
 *   by their `data-pr-url` href (product handle). Keep the first occurrence.
 *
 * Also runs once on DOMContentLoaded to clean up the initial server-rendered
 * page in case it ever contains dupes from a previous swap.
 *
 * Designed to be a no-op when there are no duplicates (the common case), so
 * it adds essentially zero overhead to normal browsing.
 */

const SEL_PRODUCT_HREF = '[data-pr-url]';

/**
 * Extract the canonical product handle from an anchor href like
 *   /products/mens-black-jacket-blk-2596-gs?_pos=1&_sid=...&_ss=r
 * Returns null for hrefs we can't parse.
 */
function handleFromHref(href) {
  if (!href) return null;
  // Strip query string and leading slash
  const path = href.split('?')[0].replace(/^\/+/, '');
  const m = path.match(/^products\/([a-z0-9\-]+)/i);
  return m ? m[1].toLowerCase() : null;
}

/**
 * Dedupe products in the entire visible main-content area.
 * Used for initial page load and on every products:update dispatch.
 *
 * Dedupes only WITHIN the current page render. Cross-page duplicates
 * (same product on page 1 and page 2) are tolerated because the server
 * pagination URL parameters already minimise overlap, and tracking seen
 * handles across pages would break the "click back to previous page" UX.
 */
function dedupeMainContent() {
  const main = document.querySelector('[main-content]');
  if (!main) return 0;
  const cards = main.querySelectorAll('.hdt-card-product');
  const seen = new Set();
  let removed = 0;
  cards.forEach((card) => {
    const a = card.querySelector(SEL_PRODUCT_HREF);
    if (!a) return;
    const handle = handleFromHref(a.getAttribute('href'));
    if (!handle) return;
    if (seen.has(handle)) {
      if (card.parentNode) {
        card.parentNode.removeChild(card);
        removed++;
      }
    } else {
      seen.add(handle);
    }
  });
  return removed;
}

// Initial cleanup once DOM is parsed (handles any server-side dupes).
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    const n = dedupeMainContent();
    if (n > 0) console.info('[rivgent-search-dedup] removed', n, 'initial duplicate product(s)');
  });
} else {
  const n = dedupeMainContent();
  if (n > 0) console.info('[rivgent-search-dedup] removed', n, 'initial duplicate product(s)');
}

// After every AJAX pagination swap, dedupe.
document.addEventListener('products:update', () => {
  // Defer one microtask so any custom elements attached during swap finish
  // before we walk the DOM.
  queueMicrotask(() => {
    const n = dedupeMainContent();
    if (n > 0) console.info('[rivgent-search-dedup] removed', n, 'duplicate product(s) after pagination');
  });
});